import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import fr from 'date-fns/locale/fr';

import useGetRequest from '../../../api/useGetRequest';
import fleetApi from '../../../services/api.service';
import useOptimizeUnassignedSteps from '../../../api/tours-planning/useOptimizeUnassignedSteps';
import useAssignStep from '../../../api/tours-planning/useAssignStep';
import useOptimizeVehicle from '../../../api/tours-planning/useOptimizeVehicle';
import useRescheduleSteps from '../../../api/tours-planning/useRescheduleSteps';
import useGetTourPlanningDetails from '../../../api/tours-planning/useGetTourPlanningDetails';
import useReturnToPool from '../../api/useReturnToPool';
import useUnassignSteps from '../../api/useUnassignSteps';
import useReorderVehicleSteps from '../../api/useReorderVehicleSteps';
import useDispatchAssignments from '../../api/useDispatchAssignments';
import GenericTableSkeleton from '../../../components/agv-generic-table/genericTableSkeleton';
import { decodeVroomGeometry } from '../../../utils/polylineDecoder';
import ManualAssignmentModal from './ManualAssignmentModal';
import RescheduleDateModal from './RescheduleDateModal';
import { ignoredVehicleLabel } from './ignoredVehicleReasons';
import DispatchModal, { describeDispatchResult } from './DispatchModal';
import PageHeader from './PageHeader';
import FiltersBar from './FiltersBar';
import TruckGroupHeader from './TruckGroupHeader';
import AssignmentRow from './AssignmentRow';
import DepotRow from './DepotRow';
import SelectionBar from './SelectionBar';
import AssignmentsMap from './AssignmentsMap';
import AssignmentDrawer from './AssignmentDrawer';
import UnavailableModal from './UnavailableModal';
import VehiclesOfTheDayPanel from './VehiclesOfTheDayPanel';
import useVehiclesOfTheDay from './useVehiclesOfTheDay';
import {
  describeTruckUnavailability,
  ELIGIBILITY,
  getAssignableTrucks,
  isTruckAssignable,
  sortTrucksOfTheDay,
} from './vehicles-of-the-day.utils';
import EditVehicleModal from '../EditVehicleModal';
import {
  COLORS,
  TABS,
  UNASSIGNED_GROUP_KEY,
  formatDuration,
  formatKm,
  formatWeight,
  getDeliveryAlerts,
  getTruckTitle,
} from './truck-assignments.utils';
import './truck-assignments.css';

moment.locale('fr');
registerLocale('fr', fr);

const OPTIMIZATION_TOAST_DURATION = 10000;

// Glisser-déposer : bande (px) en haut et en bas de la liste qui la fait
// défiler, et vitesse maximale (px par événement dragover, ~20 par seconde)
const DRAG_SCROLL_EDGE = 72;
const DRAG_SCROLL_MAX_STEP = 24;

const formatAddress = (street, zipcode, city) => {
  const parts = [street, zipcode, city].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : 'Adresse non disponible';
};

/**
 * Transforme la réponse de /tours-planning/scheduled en camions, commandes,
 * plateformes, tracés et KPI VROOM par tournée
 */
const parseScheduledData = (res) => {
  const factories = (res?.factories || [])
    .filter((factory) => factory && factory.latitude && factory.longitude)
    .map((factory) => ({
      id: factory.id || factory.factory_id,
      name: factory.name || factory.factory_name || 'Usine',
      lat: parseFloat(factory.latitude),
      lng: parseFloat(factory.longitude),
    }));

  const trucks = (res?.all_vehicles || []).map((vehicle) => ({
    id: vehicle.vehicule_id,
    factoryId: vehicle.factory_id || null,
    key: vehicle.vehicule_key || vehicle.imat,
    label: vehicle.label,
    licensePlate: vehicle.imat || 'Non spécifié',
    type: vehicle.vehicle_type || 'Non spécifié',
    capacity:
      typeof vehicle.total_capacity === 'number'
        ? vehicle.total_capacity
        : parseFloat(vehicle.total_capacity) || 1800,
    usedCapacity: 0,
    color: vehicle.color || '#cccccc',
    timeWindowStart: vehicle.time_window_start || null,
    timeWindowEnd: vehicle.time_window_end || null,
    // Véhicules du jour (lus seulement quand la feature est activée)
    eligibilityStatus: vehicle.eligibility_status || null,
    unavailabilityReason: vehicle.unavailability_reason || null,
    // Mode envoi : tournée du camion à la date, verrouillée une fois envoyée
    dispatchState: vehicle.dispatch_state || null,
    isLocked: Boolean(vehicle.dispatch_state?.is_locked),
  }));

  const kpisByTour = {};
  const routesByTour = new Map();
  const steps = res?.steps || [];

  steps.forEach((step) => {
    const tourLinked = step.tour_linked_if_any;
    if (!tourLinked?.tour_id) return;

    // Véhicule absent de all_vehicles : on l'ajoute pour ne pas perdre le groupe
    const vehicleId = tourLinked.vehicle?.vehicule_id;
    if (vehicleId && !trucks.some((t) => t.id === vehicleId)) {
      trucks.push({
        id: vehicleId,
        key: tourLinked.vehicule_key || tourLinked.vehicle?.imat,
        licensePlate: tourLinked.vehicle?.imat || 'Non spécifié',
        type: 'Non spécifié',
        capacity: 0,
        usedCapacity: 0,
        color: COLORS.primary,
        timeWindowStart: null,
        timeWindowEnd: null,
        dispatchState: null,
        isLocked: false,
      });
    }

    if (!kpisByTour[tourLinked.tour_id]) {
      const totalDuration = tourLinked.vroom_total_duration || 0;
      const travelTime =
        totalDuration -
        (tourLinked.vroom_total_service_time || 0) -
        (tourLinked.vroom_total_waiting_time || 0);
      kpisByTour[tourLinked.tour_id] = {
        totalDistance: tourLinked.vroom_total_distance || 0,
        totalDuration,
        travelTime: travelTime > 0 ? travelTime : null,
        departureAt: tourLinked.vroom_departure_at
          ? moment.utc(tourLinked.vroom_departure_at).local()
          : null,
        returnAt: tourLinked.vroom_return_at
          ? moment.utc(tourLinked.vroom_return_at).local()
          : null,
      };
    }

    if (tourLinked.geometry && !routesByTour.has(tourLinked.tour_id)) {
      const truck = trucks.find((t) => t.id === vehicleId);
      try {
        routesByTour.set(tourLinked.tour_id, {
          tourId: tourLinked.tour_id,
          vehicleId,
          vehicleKey: tourLinked.vehicule_key,
          color: truck?.color || COLORS.primary,
          coordinates: decodeVroomGeometry(tourLinked.geometry),
        });
      } catch (error) {
        console.error(
          `Erreur lors du décodage de la géométrie pour le tour ${tourLinked.tour_id}:`,
          error
        );
      }
    }
  });

  const deliveries = steps.map((step, index) => {
    const endAddress = step.raw_data?.step?.end_address;
    const stepLinked = step.step_linked_if_any;
    const tourLinked = step.tour_linked_if_any;
    const deliveryDetails = step.delivery_details || [];
    const productCount = deliveryDetails.flatMap((dd) => dd.items || []).length;
    // ETA stockée en UTC, renvoyée sans fuseau dans le JSON de /scheduled ;
    // 00:00 UTC = pas d'ETA (étape jamais optimisée ou hors horaires)
    const estimated = stepLinked?.estimated_delivery
      ? moment.utc(stepLinked.estimated_delivery)
      : null;
    const eta =
      estimated && estimated.format('HH:mm') !== '00:00'
        ? estimated.clone().local()
        : null;
    const etaTime = eta ? eta.format('HH:mm') : null;
    // ETA le lendemain de la tournée (tournée de nuit) : « 01:00 J+1 »
    const etaDayOffset =
      eta && step.scheduled_at
        ? eta
            .clone()
            .startOf('day')
            .diff(moment(step.scheduled_at).startOf('day'), 'days')
        : 0;
    const truck = trucks.find((t) => t.id === tourLinked?.vehicle?.vehicule_id);

    return {
      id: stepLinked?.step_id || `step_${index + 1}`,
      tourPlanningId: step.id,
      raw: step,
      client: endAddress?.name || 'N/A',
      city: endAddress?.city || 'Ville inconnue',
      address: formatAddress(
        endAddress?.street,
        endAddress?.zipcode,
        endAddress?.city
      ),
      blKeys: deliveryDetails
        .map((dd) => dd.delivery_key || dd.bl_key)
        .filter(Boolean),
      blCount: deliveryDetails.length || 1,
      products: `${productCount} produit${productCount > 1 ? 's' : ''}`,
      weight: parseFloat((step.total_weight || 0).toFixed(2)),
      etaTime,
      etaDayOffset,
      etaAt: eta,
      // Temps de service calculé par VROOM (min), 15 min par défaut
      serviceMinutes: Number(step.vroom_service_time) || 15,
      // Optimisée mais non placée par VROOM : ne tient pas dans les horaires
      // du camion
      isOutOfTimeWindow: Boolean(
        tourLinked?.tour_id &&
          step.vroom_optimization_date &&
          !step.vroom_arrival_time &&
          !etaTime
      ),
      etaExceedsTimeWindow: isAfterTimeWindowEnd(eta, step.scheduled_at, truck),
      vehicleId: tourLinked?.vehicle?.vehicule_id || null,
      // Camion prévu par le fichier (immatriculation) : affecté d'office à
      // l'ordonnancement, sauf s'il était indisponible ou déjà envoyé
      plannedVehicle:
        `${step.raw_data?.step?.vehicle_imat_if_any || ''}`.trim() || null,
      tourId: tourLinked?.tour_id || null,
      order: stepLinked?.order || 0,
      vroomKpis: tourLinked?.tour_id ? kpisByTour[tourLinked.tour_id] : null,
      coordinates:
        endAddress?.latitude !== undefined &&
        endAddress?.latitude !== null &&
        endAddress?.longitude !== undefined &&
        endAddress?.longitude !== null
          ? {
              lat: parseFloat(endAddress.latitude),
              lng: parseFloat(endAddress.longitude),
            }
          : null,
    };
  });

  trucks.forEach((truck) => {
    const weight = deliveries
      .filter((d) => d.vehicleId === truck.id)
      .reduce((sum, d) => sum + (d.weight || 0), 0);
    truck.usedCapacity = parseFloat(weight.toFixed(2));
  });
  trucks.sort((a, b) => String(a.key).localeCompare(String(b.key)));

  return {
    factories,
    trucks,
    deliveries,
    kpisByTour,
    routes: Array.from(routesByTour.values()),
    apiKpi: res?.kpi || null,
  };
};

/**
 * L'ETA dépasse-t-elle la fin de tournée du camion ? Une fin antérieure au
 * début (ex. 22:00 → 06:00) tombe le lendemain.
 */
const isAfterTimeWindowEnd = (eta, tourDate, truck) => {
  if (!eta || !tourDate || !truck?.timeWindowEnd) return false;
  const day = moment(tourDate).startOf('day');
  const end = moment(
    `${day.format('YYYY-MM-DD')} ${truck.timeWindowEnd}`,
    'YYYY-MM-DD HH:mm:ss'
  );
  if (
    truck.timeWindowStart &&
    truck.timeWindowEnd.slice(0, 5) <= truck.timeWindowStart.slice(0, 5)
  ) {
    end.add(1, 'day');
  }
  return eta.isAfter(end);
};

const routeFromGeometry = (truck, route) => ({
  vehicleId: truck.id,
  vehicleKey: truck.key,
  color: truck.color || COLORS.primary,
  coordinates: decodeVroomGeometry(route.geometry),
  totalDistance: route.totalDistance,
  totalDuration: route.totalDuration,
});

export default function TruckAssignments() {
  const navigate = useNavigate();
  const features = useSelector((state) => state.features);
  const canOptimizeAll =
    features?.TOUR_PLANNING?.TRUCK_ASSIGNMENTS?.VROOM_OPTIMIZATION?.enabled;
  // Véhicules du jour : le dispatcher marque les camions indisponibles à la date
  const hasVehiclesOfTheDay = Boolean(
    features?.TOUR_PLANNING?.TRUCK_ASSIGNMENTS?.VEHICLES_OF_THE_DAY?.enabled
  );
  const isUnavailableTruck = (truck) =>
    hasVehiclesOfTheDay && Boolean(truck) && !isTruckAssignable(truck);
  // Mode « envoi en exploitation » : tournées en brouillon jusqu'à l'envoi,
  // camions verrouillés une fois envoyés
  const isDispatchEnabled =
    features?.TOUR_PLANNING?.TRUCK_ASSIGNMENTS?.DISPATCH?.enabled === true;
  // Terme du client pour le point de départ et de retour des camions
  const depotLabel =
    `${features?.TOUR_PLANNING?.TRUCK_ASSIGNMENTS?.DEPOT_LABEL || ''}`.trim() ||
    'Dépôt';

  // Données
  const [deliveries, setDeliveries] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [factories, setFactories] = useState([]);
  const [kpisByTour, setKpisByTour] = useState({});
  const [apiKpi, setApiKpi] = useState(null);
  const [optimizedRoutes, setOptimizedRoutes] = useState([]);
  const [impossibleJobIds, setImpossibleJobIds] = useState([]);
  // Modale d'envoi : { onlyVehicleKey } (null = tous les camions de la date)
  const [dispatchModal, setDispatchModal] = useState(null);

  // Filtres et vue
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activeTab, setActiveTab] = useState(TABS.ALL);
  const [search, setSearch] = useState('');
  const [selectedVehicleIds, setSelectedVehicleIds] = useState(new Set());
  const [hideEmptyTrucks, setHideEmptyTrucks] = useState(false);

  // Sélection multiple et glisser-déposer
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [draggedIds, setDraggedIds] = useState(null);
  const [dropTargetKey, setDropTargetKey] = useState(null);
  // Ligne survolée pendant un glisser-déposer : { id, position: before|after }
  const [rowDropTarget, setRowDropTarget] = useState(null);
  const listRef = useRef(null);
  // Groupe « À placer » replié (clic sur son en-tête)
  const [isUnassignedCollapsed, setIsUnassignedCollapsed] = useState(false);

  // Retours d'optimisation
  const [optimizationToast, setOptimizationToast] = useState(null);
  const [unplacedBanner, setUnplacedBanner] = useState(null);

  // Modales
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [selectedDeliveryForAssignment, setSelectedDeliveryForAssignment] =
    useState(null);
  const [autoOptimizeAfterAssignment, setAutoOptimizeAfterAssignment] =
    useState(true);
  const [rescheduleIds, setRescheduleIds] = useState(null);
  // Fiche véhicule ouverte depuis le menu d'un camion (horaires, etc.)
  const [vehicleDetailsTruck, setVehicleDetailsTruck] = useState(null);

  // Fiche d'une commande (bouton « œil »)
  const [openedId, setOpenedId] = useState(null);
  const {
    isLoading: isDetailsLoading,
    data: detailsData,
    fetchDetails,
    reset: resetDetails,
  } = useGetTourPlanningDetails();

  const {
    optimizeAllSteps,
    isOptimizing,
    error: optimizationError,
    resetOptimization,
  } = useOptimizeUnassignedSteps();
  const { unassignSteps, isUnassigning } = useUnassignSteps();
  const { assignStep, isAssigning, error: assignError } = useAssignStep();
  const {
    optimizeVehicle,
    isOptimizing: isOptimizingVehicle,
    error: optimizeVehicleError,
  } = useOptimizeVehicle();
  const {
    rescheduleSteps,
    isRescheduling,
    error: rescheduleError,
  } = useRescheduleSteps();
  const { returnToPool, isReturning } = useReturnToPool();
  const { reorderVehicleSteps, isReordering } = useReorderVehicleSteps();
  const { isDispatching, previewDispatch, dispatch } = useDispatchAssignments();

  const tourDate = moment(selectedDate).format('YYYY-MM-DD');
  const {
    data: scheduledData,
    isLoading: isLoadingScheduled,
    error: scheduledError,
    mutate: mutateScheduledData,
  } = useGetRequest(
    `/tours-planning/scheduled?${new URLSearchParams({
      tour_date: tourDate,
      status: 'SCHEDULED',
    }).toString()}`,
    300
  );

  // Pastilles du calendrier : jours où l'ordonnancement a commencé (lignes à
  // placer ou camions planifiés), pour le mois affiché et ses bords
  const [calendarMonth, setCalendarMonth] = useState(() =>
    moment(selectedDate).format('YYYY-MM')
  );
  const { data: calendarData, mutate: mutateCalendar } = useGetRequest(
    `/tours-planning/calendar?${new URLSearchParams({
      from: moment(calendarMonth, 'YYYY-MM')
        .subtract(7, 'days')
        .format('YYYY-MM-DD'),
      to: moment(calendarMonth, 'YYYY-MM')
        .endOf('month')
        .add(7, 'days')
        .format('YYYY-MM-DD'),
    }).toString()}`,
    300
  );
  const calendarDays = useMemo(
    () => new Map((calendarData?.days || []).map((day) => [day.date, day])),
    [calendarData]
  );

  const refreshData = useCallback(() => {
    mutateScheduledData();
    mutateCalendar();
  }, [mutateScheduledData, mutateCalendar]);

  // Véhicules du jour : panneau, confirmation et levée d'une indisponibilité
  const vehiclesOfTheDay = useVehiclesOfTheDay({
    tourDate,
    onChanged: refreshData,
  });

  // Erreurs remontées par les hooks
  useEffect(() => {
    if (scheduledError) {
      console.error('Erreur lors du chargement des données:', scheduledError);
      toast.error('Erreur lors du chargement des données planifiées');
    }
  }, [scheduledError]);

  useEffect(() => {
    [optimizationError, assignError, optimizeVehicleError, rescheduleError]
      .filter(Boolean)
      .forEach((error) => toast.error(error));
  }, [optimizationError, assignError, optimizeVehicleError, rescheduleError]);

  // Changement de date : on repart d'un état vierge
  useEffect(() => {
    setOptimizedRoutes([]);
    setImpossibleJobIds([]);
    setSelectedVehicleIds(new Set());
    setSelectedIds(new Set());
    setUnplacedBanner(null);
    setOptimizationToast(null);
    setOpenedId(null);
    setDispatchModal(null);
    resetOptimization();
  }, [selectedDate]);

  useEffect(() => {
    if (!scheduledData) return;
    const parsed = parseScheduledData(scheduledData);
    setFactories(parsed.factories);
    setTrucks(
      hasVehiclesOfTheDay ? sortTrucksOfTheDay(parsed.trucks) : parsed.trucks
    );
    setDeliveries(parsed.deliveries);
    setKpisByTour(parsed.kpisByTour);
    setApiKpi(parsed.apiKpi);

    // Les tracés de la BDD (les plus récents) écrasent ceux gardés en mémoire
    if (parsed.routes.length > 0) {
      setOptimizedRoutes((prevRoutes) => {
        const routesMap = new Map();
        [...prevRoutes, ...parsed.routes].forEach((route) => {
          const key = route.vehicleKey || String(route.vehicleId);
          if (key) routesMap.set(key, route);
        });
        return Array.from(routesMap.values());
      });
    }

    // On retire de la sélection les commandes qui ont disparu
    const existingIds = new Set(parsed.deliveries.map((d) => d.tourPlanningId));
    setSelectedIds((prev) => {
      const next = new Set([...prev].filter((id) => existingIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [scheduledData, hasVehiclesOfTheDay]);

  useEffect(() => {
    if (!optimizationToast) return undefined;
    const timer = setTimeout(
      () => setOptimizationToast(null),
      OPTIMIZATION_TOAST_DURATION
    );
    return () => clearTimeout(timer);
  }, [optimizationToast]);

  const replaceVehicleRoute = useCallback((truck, route) => {
    if (!route?.geometry) return;
    try {
      const newRoute = routeFromGeometry(truck, route);
      setOptimizedRoutes((prevRoutes) => [
        ...prevRoutes.filter(
          (r) => r.vehicleKey !== truck.key && r.vehicleId !== truck.id
        ),
        newRoute,
      ]);
    } catch (error) {
      console.error('Erreur lors du décodage de la géométrie:', error);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Données dérivées
  // ---------------------------------------------------------------------------

  const truckById = useMemo(
    () => new Map(trucks.map((truck) => [truck.id, truck])),
    [trucks]
  );

  // Camions envoyés en exploitation (mode envoi) : plus rien ne s'y modifie
  const lockedTruckIds = useMemo(
    () =>
      new Set(
        trucks.filter((truck) => truck.isLocked).map((truck) => truck.id)
      ),
    [trucks]
  );
  const editableTrucks = useMemo(
    () => trucks.filter((truck) => !truck.isLocked),
    [trucks]
  );
  const isDeliveryLocked = useCallback(
    (delivery) =>
      Boolean(delivery?.vehicleId && lockedTruckIds.has(delivery.vehicleId)),
    [lockedTruckIds]
  );
  const lockedTourPlanningIds = useMemo(
    () =>
      new Set(
        deliveries
          .filter((delivery) => isDeliveryLocked(delivery))
          .map((delivery) => delivery.tourPlanningId)
      ),
    [deliveries, isDeliveryLocked]
  );

  const alertsById = useMemo(() => {
    const map = new Map();
    deliveries.forEach((delivery) => {
      map.set(
        delivery.tourPlanningId,
        getDeliveryAlerts(delivery, impossibleJobIds)
      );
    });
    return map;
  }, [deliveries, impossibleJobIds]);

  const alertsCount = useMemo(
    () => [...alertsById.values()].filter((alerts) => alerts.length > 0).length,
    [alertsById]
  );

  const truckCounts = useMemo(() => {
    const counts = {};
    deliveries.forEach((delivery) => {
      if (delivery.vehicleId) {
        counts[delivery.vehicleId] = (counts[delivery.vehicleId] || 0) + 1;
      }
    });
    return counts;
  }, [deliveries]);

  const unassignedDeliveries = useMemo(
    () => deliveries.filter((delivery) => !delivery.vehicleId),
    [deliveries]
  );

  // Camions en préparation (affectés, pas encore envoyés) : ce que l'envoi enverrait
  const draftTrucksCount = useMemo(
    () =>
      editableTrucks.filter((truck) => (truckCounts[truck.id] || 0) > 0).length,
    [editableTrucks, truckCounts]
  );

  const plannedDistance = useMemo(() => {
    const vroomDistance = Object.values(kpisByTour).reduce(
      (sum, kpi) => sum + (kpi.totalDistance || 0),
      0
    );
    if (vroomDistance > 0) return vroomDistance;
    // Le KPI renvoyé par l'API est exprimé en km
    return (apiKpi?.totalDistance || 0) * 1000;
  }, [kpisByTour, apiKpi]);

  const headerSummary = useMemo(() => {
    const usedTrucks = Object.keys(truckCounts).length;
    const parts = [
      `${deliveries.length} commande${deliveries.length > 1 ? 's' : ''}`,
      `${unassignedDeliveries.length} à placer`,
      `${usedTrucks} camion${usedTrucks > 1 ? 's' : ''} sur ${trucks.length}`,
    ];
    if (plannedDistance > 0) parts.push(`${formatKm(plannedDistance)} prévus`);
    return parts.join(' · ');
  }, [
    deliveries.length,
    unassignedDeliveries.length,
    truckCounts,
    trucks.length,
    plannedDistance,
  ]);

  const matchesFilters = useCallback(
    (delivery) => {
      if (activeTab === TABS.UNASSIGNED && delivery.vehicleId) return false;
      if (activeTab === TABS.ASSIGNED && !delivery.vehicleId) return false;
      if (
        activeTab === TABS.ALERTS &&
        (alertsById.get(delivery.tourPlanningId) || []).length === 0
      ) {
        return false;
      }

      const query = search.trim().toLowerCase();
      if (!query) return true;
      return [
        delivery.client,
        delivery.city,
        delivery.address,
        ...delivery.blKeys,
      ]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query));
    },
    [activeTab, alertsById, search]
  );

  const groups = useMemo(() => {
    const hasSearch = search.trim().length > 0;
    const result = [];

    const unassignedRows = unassignedDeliveries.filter(matchesFilters);
    if (
      activeTab !== TABS.ASSIGNED &&
      (unassignedRows.length > 0 ||
        (unassignedDeliveries.length > 0 &&
          !hasSearch &&
          activeTab !== TABS.ALERTS))
    ) {
      result.push({
        key: UNASSIGNED_GROUP_KEY,
        isUnassigned: true,
        all: unassignedDeliveries,
        rows: unassignedRows,
      });
    }

    if (activeTab === TABS.UNASSIGNED) return result;

    trucks
      .filter(
        (truck) =>
          selectedVehicleIds.size === 0 || selectedVehicleIds.has(truck.id)
      )
      .forEach((truck) => {
        const all = deliveries
          .filter((delivery) => delivery.vehicleId === truck.id)
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        const rows = all.filter(matchesFilters);
        const showEmpty =
          activeTab === TABS.ALL && !hasSearch && !hideEmptyTrucks;

        if (rows.length > 0 || (all.length === 0 && showEmpty)) {
          result.push({ key: truck.id, truck, all, rows });
        }
      });

    return result;
  }, [
    activeTab,
    deliveries,
    hideEmptyTrucks,
    matchesFilters,
    search,
    selectedVehicleIds,
    trucks,
    unassignedDeliveries,
  ]);

  const openedDelivery = useMemo(
    () => deliveries.find((d) => d.tourPlanningId === openedId) || null,
    [deliveries, openedId]
  );

  const openDelivery = (delivery) => {
    setOpenedId(delivery.tourPlanningId);
    fetchDetails(delivery.tourPlanningId);
  };

  const closeDrawer = useCallback(() => {
    setOpenedId(null);
    resetDetails();
  }, [resetDetails]);

  const selectedDeliveries = useMemo(
    () => deliveries.filter((d) => selectedIds.has(d.tourPlanningId)),
    [deliveries, selectedIds]
  );

  const isBusy =
    isAssigning ||
    isUnassigning ||
    isRescheduling ||
    isOptimizingVehicle ||
    isReturning ||
    isReordering;

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  const handleOptimizeRoutes = async () => {
    try {
      const result = await optimizeAllSteps({ tourDate });
      if (!result.success) return;

      const data = result.data || {};
      const routesWithGeometry = (data.routes || [])
        .map((route) => {
          const truck = trucks.find(
            (t) => t.id === route.vehicleId || t.key === route.vehicleKey
          );
          if (!route.geometry) return null;
          try {
            return {
              ...routeFromGeometry(
                truck || { id: route.vehicleId, key: route.vehicleKey },
                route
              ),
              color: truck?.color || COLORS.primary,
            };
          } catch (error) {
            console.error(
              `Erreur lors du décodage de la géométrie pour ${route.vehicleKey}:`,
              error
            );
            return null;
          }
        })
        .filter(Boolean);
      if (routesWithGeometry.length > 0) {
        setOptimizedRoutes(routesWithGeometry);
      }

      const summary = data.summary || {};
      const placedCount = (data.routes || []).reduce(
        (sum, route) =>
          sum + (route.steps || []).filter((s) => s.type === 'job').length,
        0
      );
      setOptimizationToast({
        routes: summary.totalRoutes || (data.routes || []).length,
        orders: placedCount,
        distance: summary.totalDistance || 0,
        duration: summary.totalDuration || 0,
      });

      const impossibleJobs = data.impossibleJobs || [];
      setImpossibleJobIds(impossibleJobs.map((job) => job.tourPlanningId));
      const unplacedCount = Math.max(
        (data.unassignedJobs || []).length,
        impossibleJobs.length
      );
      setUnplacedBanner(
        unplacedCount > 0
          ? { unplaced: unplacedCount, overCapacity: impossibleJobs.length }
          : null
      );

      if (data.ignoredVehicles?.length > 0) {
        const ignoredDetails = data.ignoredVehicles
          .map(
            (v) =>
              `• ${v.licensePlate} (${v.vehicleKey}): ${ignoredVehicleLabel(v)}`
          )
          .join('\n');
        toast.warning(
          `${data.ignoredVehicles.length} véhicule(s) non utilisé(s) :\n${ignoredDetails}`,
          { autoClose: 10000 }
        );
      }

      refreshData();
    } catch (error) {
      console.error("Erreur lors de l'optimisation:", error);
      toast.error(
        error.message || "Erreur lors de l'optimisation des tournées"
      );
    }
  };

  const handleOptimizeVehicle = async (truck) => {
    if (!truck?.id) {
      toast.error("Impossible d'optimiser ce véhicule");
      return;
    }

    try {
      const result = await optimizeVehicle({ vehicleId: truck.id, tourDate });
      if (!result.success) return;

      replaceVehicleRoute(truck, result.data?.route);

      // Petit délai pour laisser la transaction SQL se terminer
      setTimeout(() => refreshData(), 200);
    } catch (error) {
      console.error("Erreur lors de l'optimisation du véhicule:", error);
    }
  };

  /**
   * Envoi en exploitation des camions cochés dans la modale : leurs tournées
   * deviennent visibles (web, mobile) et ils sont verrouillés. Un camion en
   * échec garde ses affectations modifiables, son motif est affiché.
   */
  const handleDispatch = async (vehiculeKeys) => {
    const result = await dispatch(tourDate, vehiculeKeys);
    if (!result) return false;

    if ((result.tours || []).length > 0) {
      toast.success(describeDispatchResult(result), { autoClose: 8000 });
    }
    (result.failures || []).forEach((failure) =>
      toast.error(
        failure.message.includes(failure.vehicule_key)
          ? failure.message
          : `${failure.vehicule_key} non envoyé : ${failure.message}`,
        { autoClose: 10000 }
      )
    );
    refreshData();
    return true;
  };

  /**
   * Affecte des commandes à un camion (ajout en fin de tournée, avec
   * optimisation éventuelle). Les commandes déjà sur ce camion sont ignorées.
   */
  const assignDeliveries = async (targetDeliveries, truck) => {
    const toAssign = targetDeliveries.filter((d) => d.vehicleId !== truck.id);
    if (toAssign.length === 0) return false;

    const result = await assignStep({
      tourPlanningIds: toAssign.map((d) => d.tourPlanningId),
      vehicleKey: truck.key,
      tourDate,
      optimize: autoOptimizeAfterAssignment,
    });
    if (!result.success) return false;

    const optimization = result.data?.optimization;
    replaceVehicleRoute(truck, optimization?.route);

    refreshData();
    return true;
  };

  /**
   * Désaffecte des commandes (retour dans « À placer ») en un seul appel :
   * chaque camion touché n'est ré-optimisé qu'une fois
   */
  const unassignDeliveries = async (targetDeliveries) => {
    const toUnassign = targetDeliveries.filter((d) => d.tourId && d.id);
    if (toUnassign.length === 0) return;

    try {
      await unassignSteps(toUnassign.map((d) => d.tourPlanningId));
    } catch (error) {
      toast.error(error.message);
    }
    refreshData();
  };

  const handleUnassignDelivery = (delivery) => {
    const truck = truckById.get(delivery.vehicleId);
    if (
      window.confirm(
        `Désaffecter la commande de ${delivery.client} du camion ${
          truck?.key || ''
        } ?`
      )
    ) {
      unassignDeliveries([delivery]);
    }
  };

  /**
   * Renvoie des commandes au puits : elles quittent la date (et leur camion,
   * qui est ré-optimisé) et redeviennent ordonnançables depuis le puits
   */
  const handleReturnToPool = async (targetDeliveries) => {
    if (targetDeliveries.length === 0) return;
    const label =
      targetDeliveries.length > 1
        ? `les ${targetDeliveries.length} commandes sélectionnées`
        : `la commande de ${targetDeliveries[0].client}`;
    if (!window.confirm(`Renvoyer ${label} au puits de commandes ?`)) return;

    try {
      const { returnedCount } = await returnToPool(
        targetDeliveries.map((d) => d.tourPlanningId)
      );
      toast.success(
        `${returnedCount} commande${returnedCount > 1 ? 's' : ''} renvoyée${
          returnedCount > 1 ? 's' : ''
        } au puits`
      );
      const returnedIds = new Set(
        targetDeliveries.map((d) => d.tourPlanningId)
      );
      setSelectedIds(
        (prev) => new Set([...prev].filter((id) => !returnedIds.has(id)))
      );
      if (returnedIds.has(openedId)) closeDrawer();
      refreshData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEmptyTruck = (truck, truckDeliveries) => {
    if (
      window.confirm(
        `Désaffecter les ${truckDeliveries.length} commande(s) du camion ${truck.key} ?`
      )
    ) {
      unassignDeliveries(truckDeliveries);
    }
  };

  const handleOpenAssignmentModal = (delivery) => {
    if (isDeliveryLocked(delivery)) {
      toast.warning(
        'Ce camion a été envoyé en exploitation : ses commandes ne se déplacent plus depuis cet écran.'
      );
      return;
    }
    setSelectedDeliveryForAssignment([delivery]);
    setShowAssignmentModal(true);
  };

  // Barre de sélection : même modale, pour toutes les commandes cochées
  const handleOpenBulkAssignmentModal = () => {
    setSelectedDeliveryForAssignment(selectedDeliveries);
    setShowAssignmentModal(true);
  };

  const handleCloseAssignmentModal = () => {
    if (isAssigning) return;
    setShowAssignmentModal(false);
    setSelectedDeliveryForAssignment(null);
  };

  const handleManualAssignment = async (truck) => {
    if (!selectedDeliveryForAssignment?.length || !truck) {
      toast.error("Impossible d'affecter cette commande");
      return;
    }
    const success = await assignDeliveries(
      selectedDeliveryForAssignment,
      truck
    );
    if (!success) return;
    // Les commandes affectées sortent de la sélection
    const assignedIds = new Set(
      selectedDeliveryForAssignment.map((d) => d.tourPlanningId)
    );
    setSelectedIds(
      (prev) => new Set([...prev].filter((id) => !assignedIds.has(id)))
    );
    setShowAssignmentModal(false);
    setSelectedDeliveryForAssignment(null);
  };

  const handleReschedule = async (newDate) => {
    if (!rescheduleIds || rescheduleIds.length === 0) return;

    try {
      const result = await rescheduleSteps({
        tourPlanningIds: rescheduleIds,
        newTourDate: moment(newDate).format('YYYY-MM-DD'),
      });
      if (result.success) {
        toast.success(
          `${result.data.rescheduledCount} étape(s) replanifiée(s) pour le ${moment(
            newDate
          ).format('DD/MM/YYYY')}`
        );
        setRescheduleIds(null);
        setSelectedIds(new Set());
        refreshData();
      }
    } catch (error) {
      console.error('Erreur lors de la replanification:', error);
    }
  };

  const handleExportAssignments = () => {
    if (!scheduledData?.steps?.length) {
      toast.warning('Aucune donnée à exporter');
      return;
    }

    try {
      const exportData = [];
      // Date d'expédition = date de planification
      const expeditionDate = moment(selectedDate).format('DD/MM/YYYY');

      scheduledData.steps.forEach((step) => {
        const truckName =
          step.tour_linked_if_any?.vehicle?.imat || 'Non affecté';
        const clientName = step.raw_data?.step?.end_address?.name || 'N/A';
        const stepDeliveryDetails = step.bls || step.delivery_details || [];

        if (stepDeliveryDetails.length === 0) {
          exportData.push({
            NUM_COMMANDE: step.data_key || 'N/A',
            CAMION: truckName,
            DATE_D_EXPEDITION: expeditionDate,
            NUM_DE_LIGNE_DE_CMD: '1',
            CLIENT: clientName,
            POIDS_KG: step.total_weight || 'N/A',
          });
          return;
        }

        stepDeliveryDetails.forEach((dd) => {
          const ddKey = dd.delivery_key || dd.bl_key || 'N/A';
          if (!dd.items || dd.items.length === 0) {
            exportData.push({
              NUM_COMMANDE: ddKey,
              CAMION: truckName,
              DATE_D_EXPEDITION: expeditionDate,
              NUM_DE_LIGNE_DE_CMD: '1',
              CLIENT: clientName,
              POIDS_KG: 'N/A',
            });
            return;
          }
          dd.items.forEach((item, index) => {
            exportData.push({
              NUM_COMMANDE: ddKey,
              CAMION: truckName,
              DATE_D_EXPEDITION: expeditionDate,
              NUM_DE_LIGNE_DE_CMD:
                item.line_number || item.item_number || index + 1,
              CLIENT: clientName,
              POIDS_KG: item.weight || 'N/A',
            });
          });
        });
      });

      const headers = Object.keys(exportData[0]);
      const csvContent = [
        headers.join(';'),
        ...exportData.map((row) =>
          headers
            .map((header) => {
              const value = row[header];
              if (
                typeof value === 'string' &&
                (value.includes(';') ||
                  value.includes('"') ||
                  value.includes('\n'))
              ) {
                return `"${value.replace(/"/g, '""')}"`;
              }
              return value;
            })
            .join(';')
        ),
      ].join('\n');

      const blob = new Blob(['﻿' + csvContent], {
        type: 'text/csv;charset=utf-8;',
      });
      const link = document.createElement('a');
      link.setAttribute('href', URL.createObjectURL(blob));
      link.setAttribute('download', `affectations_camions_${tourDate}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Erreur lors de l'export:", error);
      toast.error("Erreur lors de l'export des données");
    }
  };

  // ---------------------------------------------------------------------------
  // Sélection et glisser-déposer
  // ---------------------------------------------------------------------------

  const toggleSelection = (ids, shouldSelect) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      // Les commandes d'un camion envoyé en exploitation ne se sélectionnent pas
      ids
        .filter((id) => !lockedTourPlanningIds.has(id))
        .forEach((id) => (shouldSelect ? next.add(id) : next.delete(id)));
      return next;
    });
  };

  const getGroupSelectionState = (rows) => {
    if (rows.length === 0) return 'none';
    const count = rows.filter((d) => selectedIds.has(d.tourPlanningId)).length;
    if (count === 0) return 'none';
    return count === rows.length ? 'all' : 'some';
  };

  const handleDragStart = (event, delivery) => {
    // Glisser une ligne cochée déplace toute la sélection
    const ids = selectedIds.has(delivery.tourPlanningId)
      ? [...selectedIds]
      : [delivery.tourPlanningId];
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', ids.join(','));
    setDraggedIds(ids);
  };

  /**
   * Pendant un glisser-déposer, approcher le bord haut ou bas de la liste la
   * fait défiler (d'autant plus vite que le curseur est près du bord). En
   * capture : les lignes arrêtent la propagation de dragover.
   */
  const handleListDragOverCapture = (event) => {
    const list = listRef.current;
    if (!draggedIds || !list) return;
    const rect = list.getBoundingClientRect();
    const fromTop = event.clientY - rect.top;
    const fromBottom = rect.bottom - event.clientY;
    const edgeRatio = (distance) =>
      Math.min(
        1,
        Math.max(0, (DRAG_SCROLL_EDGE - distance) / DRAG_SCROLL_EDGE)
      );
    if (fromTop < DRAG_SCROLL_EDGE) {
      list.scrollTop -= Math.ceil(DRAG_SCROLL_MAX_STEP * edgeRatio(fromTop));
    } else if (fromBottom < DRAG_SCROLL_EDGE) {
      list.scrollTop += Math.ceil(DRAG_SCROLL_MAX_STEP * edgeRatio(fromBottom));
    }
  };

  const handleDragEnd = () => {
    setDraggedIds(null);
    setDropTargetKey(null);
    setRowDropTarget(null);
  };

  /**
   * Nouvel ordre de passage d'un camion : les commandes glissées sont
   * insérées avant ou après la ligne cible, puis l'API recalcule les ETA et
   * le tracé en suivant cet ordre
   */
  const reorderTruck = async (
    truck,
    truckDeliveries,
    moved,
    target,
    position
  ) => {
    const movedIds = new Set(moved.map((d) => d.tourPlanningId));
    const remaining = truckDeliveries.filter(
      (d) => !movedIds.has(d.tourPlanningId)
    );
    const targetIndex = remaining.findIndex(
      (d) => d.tourPlanningId === target.tourPlanningId
    );
    const insertAt = position === 'after' ? targetIndex + 1 : targetIndex;
    const ordered = [
      ...remaining.slice(0, insertAt),
      ...truckDeliveries.filter((d) => movedIds.has(d.tourPlanningId)),
      ...remaining.slice(insertAt),
    ];
    if (ordered.every((d, index) => d === truckDeliveries[index])) return;

    try {
      const result = await reorderVehicleSteps({
        vehicleId: truck.id,
        tourDate,
        orderedStepIds: ordered.map((d) => d.id),
      });
      replaceVehicleRoute(truck, result.route);
      const late = result.outOfTimeWindowStepIds?.length || 0;
      if (late > 0) {
        toast.warning(
          `${truck.key} : ${late} commande${late > 1 ? 's' : ''} hors horaires avec cet ordre`
        );
      }
      refreshData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  /**
   * Refuse un dépôt qui ferait dépasser la capacité du camion (l'affectation
   * reste possible depuis la modale, qui affiche la charge)
   */
  const isOverCapacity = (truck, dropped) => {
    if (!truck.capacity) return false;
    const added = dropped
      .filter((d) => d.vehicleId !== truck.id)
      .reduce((sum, d) => sum + (d.weight || 0), 0);
    if (truck.usedCapacity + added <= truck.capacity) return false;
    toast.error(
      `Capacité de ${truck.key} dépassée (${formatWeight(
        truck.usedCapacity + added
      )} pour ${formatWeight(truck.capacity)}) : utilisez « Affecter à un camion… » pour forcer`
    );
    return true;
  };

  /**
   * Véhicules du jour : refuse un dépôt sur un camion indisponible à la date
   * (l'API le refuserait aussi, en 409 VEHICLE_NOT_ELIGIBLE)
   */
  const isRefusedDrop = (truck) => {
    if (!isUnavailableTruck(truck)) return false;
    toast.error(
      `${truck.key} : ${describeTruckUnavailability(truck)}. Affectez les commandes à un autre camion.`
    );
    return true;
  };

  /**
   * Dépôt sur une ligne d'un autre camion : affectation sans optimisation,
   * puis insertion des commandes à la position de dépôt (ETA recalculées)
   */
  const assignAtPosition = async (group, dropped, target, position) => {
    const truck = group.truck;
    const toAssign = dropped.filter((d) => d.vehicleId !== truck.id);
    const result = await assignStep({
      tourPlanningIds: toAssign.map((d) => d.tourPlanningId),
      vehicleKey: truck.key,
      tourDate,
      optimize: false,
    });
    if (!result.success) return;
    setSelectedIds(new Set());

    try {
      // Les étapes viennent d'être créées : on relit la tournée du camion
      const response = await fleetApi.get(
        `/tours-planning/scheduled?${new URLSearchParams({
          tour_date: tourDate,
          status: 'SCHEDULED',
        }).toString()}`
      );
      const fresh = parseScheduledData(response.data).deliveries;
      const movedIds = new Set(toAssign.map((d) => d.tourPlanningId));
      const truckDeliveries = fresh
        .filter((d) => d.vehicleId === truck.id)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      const moved = truckDeliveries.filter((d) =>
        movedIds.has(d.tourPlanningId)
      );
      const freshTarget = truckDeliveries.find(
        (d) => d.tourPlanningId === target.tourPlanningId
      );
      if (freshTarget && moved.length > 0) {
        await reorderTruck(
          truck,
          truckDeliveries,
          moved,
          freshTarget,
          position
        );
        return;
      }
    } catch (error) {
      console.error("Erreur lors de l'insertion à la position:", error);
    }
    refreshData();
  };

  /**
   * Cible de dépôt sur une ligne de camion : réordonnancement si les
   * commandes glissées sont déjà sur ce camion, affectation sinon
   */
  const getRowDropHandlers = (delivery, group) => {
    // Un camion envoyé en exploitation ne reçoit ni ne réordonne plus rien
    if (!group.truck || group.truck.isLocked) return undefined;
    return {
      onDragOver: (event) => {
        if (!draggedIds) return;
        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer.dropEffect = 'move';
        const rect = event.currentTarget.getBoundingClientRect();
        const position =
          event.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
        if (
          rowDropTarget?.id !== delivery.tourPlanningId ||
          rowDropTarget?.position !== position
        ) {
          setRowDropTarget({ id: delivery.tourPlanningId, position });
        }
      },
      onDrop: async (event) => {
        event.preventDefault();
        event.stopPropagation();
        const ids = new Set(draggedIds || []);
        const position = rowDropTarget?.position || 'before';
        handleDragEnd();
        const dropped = deliveries.filter((d) => ids.has(d.tourPlanningId));
        if (dropped.length === 0 || ids.has(delivery.tourPlanningId)) return;

        if (dropped.every((d) => d.vehicleId === group.truck.id)) {
          await reorderTruck(
            group.truck,
            group.all,
            dropped,
            delivery,
            position
          );
        } else if (
          !isRefusedDrop(group.truck) &&
          !isOverCapacity(group.truck, dropped)
        ) {
          await assignAtPosition(group, dropped, delivery, position);
        }
      },
    };
  };

  const getDropHandlers = (groupKey, truck) => ({
    dropState:
      draggedIds && dropTargetKey === groupKey
        ? isUnavailableTruck(truck)
          ? 'refused'
          : 'over'
        : null,
    onDragOver: (event) => {
      if (!draggedIds) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      if (dropTargetKey !== groupKey) setDropTargetKey(groupKey);
    },
    onDragLeave: (event) => {
      if (!event.currentTarget.contains(event.relatedTarget)) {
        setDropTargetKey(null);
      }
    },
    onDrop: async (event) => {
      event.preventDefault();
      const ids = new Set(draggedIds || []);
      handleDragEnd();
      const dropped = deliveries.filter((d) => ids.has(d.tourPlanningId));
      if (dropped.length === 0) return;

      if (truck) {
        if (isRefusedDrop(truck) || isOverCapacity(truck, dropped)) return;
        const success = await assignDeliveries(dropped, truck);
        if (success) setSelectedIds(new Set());
      } else {
        await unassignDeliveries(dropped);
        setSelectedIds(new Set());
      }
    },
  });

  // ---------------------------------------------------------------------------
  // Menus
  // ---------------------------------------------------------------------------

  const getRowMenuItems = (delivery) => {
    const isAssigned = Boolean(delivery.vehicleId);
    // Camion envoyé en exploitation : la commande ne se modifie plus d'ici
    const isLocked = isDeliveryLocked(delivery);
    const lockedHint = isLocked ? 'camion envoyé' : undefined;
    const plannedTruck =
      !isAssigned && delivery.plannedVehicle
        ? trucks.find(
            (truck) =>
              truck.licensePlate === delivery.plannedVehicle ||
              truck.key === delivery.plannedVehicle
          )
        : null;
    const plannedTruckRefusal =
      plannedTruck &&
      (plannedTruck.isLocked
        ? 'camion envoyé'
        : isUnavailableTruck(plannedTruck)
          ? 'indisponible'
          : null);

    return [
      ...(plannedTruck
        ? [
            {
              label: `Affecter au camion prévu (${plannedTruck.key})`,
              hint: plannedTruckRefusal || undefined,
              disabled: isAssigning || Boolean(plannedTruckRefusal),
              onClick: () => assignDeliveries([delivery], plannedTruck),
            },
          ]
        : []),
      {
        label: isAssigned ? 'Déplacer vers…' : 'Affecter à un camion…',
        chevron: true,
        hint: lockedHint,
        disabled: isAssigning || isLocked,
        onClick: () => handleOpenAssignmentModal(delivery),
      },
      {
        // Retire la commande du camion : elle repasse dans « À placer », à
        // la même date (elle ne revient pas dans le puits)
        label: 'Désaffecter',
        hint:
          lockedHint || (isAssigned ? 'vers « À placer »' : 'déjà à placer'),
        disabled: !isAssigned || isUnassigning || isLocked,
        onClick: () => handleUnassignDelivery(delivery),
      },
      {
        label: 'Renvoyer au puits…',
        hint: lockedHint,
        disabled: isReturning || isLocked,
        onClick: () => handleReturnToPool([delivery]),
      },
      { separator: true },
      {
        // Une commande affectée est d'abord retirée de son camion
        label: 'Replanifier…',
        hint: lockedHint,
        disabled: isRescheduling || isLocked,
        onClick: () => setRescheduleIds([delivery.tourPlanningId]),
      },
    ];
  };

  // Véhicules du jour : marquer indisponible / rendre disponible à la date
  const getVehiclesOfTheDayMenuItems = (truck, truckDeliveries) => {
    if (!hasVehiclesOfTheDay) return [];
    const isPast = tourDate < moment().format('YYYY-MM-DD');
    if (truck.eligibilityStatus === ELIGIBILITY.UNAVAILABLE) {
      return [
        {
          label: 'Rendre disponible ce jour',
          hint: truck.unavailabilityReason || undefined,
          disabled: isPast || vehiclesOfTheDay.isSaving,
          onClick: () => vehiclesOfTheDay.makeAvailable(truck),
        },
      ];
    }
    if (!isTruckAssignable(truck)) return [];
    // Refusé par l'API (409) : camion envoyé, ou commandes encore affectées
    let blockedHint;
    if (truck.isLocked) blockedHint = 'camion envoyé';
    else if (truckDeliveries.length > 0) blockedHint = 'commandes à déplacer';
    return [
      {
        label: 'Marquer indisponible ce jour…',
        hint: blockedHint,
        disabled: isPast || Boolean(blockedHint),
        onClick: () =>
          vehiclesOfTheDay.openMarkUnavailable({
            key: truck.key,
            label: truck.label,
            licensePlate: truck.licensePlate,
            assignedCount: truckDeliveries.length,
          }),
      },
    ];
  };

  const getTruckMenuItems = (truck, truckDeliveries) => [
    {
      label: 'Voir / modifier le camion…',
      onClick: () => setVehicleDetailsTruck(truck),
    },
    ...getVehiclesOfTheDayMenuItems(truck, truckDeliveries),
    { separator: true },
    {
      label: 'Vider le camion…',
      danger: true,
      hint: truck.isLocked ? 'camion envoyé' : undefined,
      disabled: truckDeliveries.length === 0 || isUnassigning || truck.isLocked,
      onClick: () => handleEmptyTruck(truck, truckDeliveries),
    },
    ...(isDispatchEnabled
      ? [
          { separator: true },
          {
            label: 'Envoyer ce camion en exploitation…',
            hint: truck.isLocked ? 'déjà envoyé' : undefined,
            disabled:
              truck.isLocked || truckDeliveries.length === 0 || isDispatching,
            onClick: () => setDispatchModal({ onlyVehicleKey: truck.key }),
          },
        ]
      : []),
  ];

  const getUnassignedMenuItems = (rows) => [
    ...(canOptimizeAll
      ? [
          {
            label: 'Optimiser les tournées',
            disabled: isOptimizing || rows.length === 0,
            onClick: handleOptimizeRoutes,
          },
        ]
      : []),
    {
      label: 'Replanifier toutes les commandes à placer…',
      disabled: rows.length === 0 || isRescheduling,
      onClick: () =>
        setRescheduleIds(rows.map((delivery) => delivery.tourPlanningId)),
    },
  ];

  // ---------------------------------------------------------------------------
  // Rendu
  // ---------------------------------------------------------------------------

  /**
   * Départ du dépôt et retour au dépôt d'un camion qui a des commandes : heures
   * de VROOM (horaire du camion à défaut pour le départ), trajet jusqu'à la
   * première étape et depuis la dernière (après son temps de service)
   */
  const getDepotRows = (group) => {
    if (group.isUnassigned || group.all.length === 0) return null;
    const { truck } = group;
    const first = group.all[0];
    const last = group.all[group.all.length - 1];
    const kpi = kpisByTour[first.tourId] || {};
    const depot =
      factories.find((factory) => factory.id === truck.factoryId) ||
      factories[0];
    const dayOf = (value) =>
      value
        ? value
            .clone()
            .startOf('day')
            .diff(moment(selectedDate).startOf('day'), 'days')
        : 0;

    const departure =
      kpi.departureAt ||
      (truck.timeWindowStart
        ? moment(`${tourDate} ${truck.timeWindowStart}`, 'YYYY-MM-DD HH:mm:ss')
        : null);
    const firstLeg =
      departure && first.etaAt ? first.etaAt.diff(departure, 'minutes') : null;
    const returnLeg =
      kpi.returnAt && last.etaAt
        ? kpi.returnAt.diff(
            last.etaAt.clone().add(last.serviceMinutes, 'minutes'),
            'minutes'
          )
        : null;

    return {
      departure: (
        <DepotRow
          kind="departure"
          depotLabel={depotLabel}
          depotName={depot?.name}
          time={departure ? departure.format('HH:mm') : null}
          legMinutes={firstLeg}
          firstOrder={first.order || 1}
          hint={!kpi.departureAt && departure ? 'horaire du camion' : undefined}
        />
      ),
      return: (
        <DepotRow
          kind="return"
          depotLabel={depotLabel}
          depotName={depot?.name}
          time={kpi.returnAt ? kpi.returnAt.format('HH:mm') : null}
          dayOffset={dayOf(kpi.returnAt)}
          legMinutes={returnLeg}
          lastOrder={last.order || group.all.length}
          hint={
            kpi.returnAt ? undefined : 'calculé à la prochaine optimisation'
          }
        />
      ),
    };
  };

  const renderGroup = (group) => {
    const depotRows = getDepotRows(group);
    const blCount = group.all.reduce((sum, d) => sum + (d.blCount || 1), 0);
    const weight = group.all.reduce((sum, d) => sum + (d.weight || 0), 0);
    const visibleIds = group.rows.map((d) => d.tourPlanningId);
    const dropHandlers = getDropHandlers(group.key, group.truck);

    let header;
    if (group.isUnassigned) {
      const withoutCoordinates = group.all.filter((d) => !d.coordinates).length;
      header = (
        <TruckGroupHeader
          isUnassigned
          title="À placer"
          color={COLORS.unassigned}
          summary={[
            `${blCount} BL`,
            formatWeight(weight),
            withoutCoordinates > 0 && `${withoutCoordinates} sans coordonnées`,
          ]
            .filter(Boolean)
            .join(' · ')}
          selectionState={getGroupSelectionState(group.rows)}
          onToggleSelect={(checked) => toggleSelection(visibleIds, checked)}
          action={
            canOptimizeAll
              ? {
                  label: isOptimizing
                    ? 'Optimisation en cours…'
                    : 'Optimiser les tournées',
                  onClick: handleOptimizeRoutes,
                  disabled: isOptimizing || group.all.length === 0,
                }
              : null
          }
          menuItems={getUnassignedMenuItems(group.all)}
          collapsed={isUnassignedCollapsed}
          onToggleCollapse={() => setIsUnassignedCollapsed((prev) => !prev)}
          {...dropHandlers}
        />
      );
    } else {
      const { truck } = group;
      const distance = group.all.find((d) => d.vroomKpis)?.vroomKpis
        ?.totalDistance;
      const hasNoTimeWindow = !truck.timeWindowStart || !truck.timeWindowEnd;
      // Véhicules du jour : camion grisé, motif et commandes à déplacer
      const isUnavailable = isUnavailableTruck(truck);
      const dispatchedAt = truck.dispatchState?.dispatched_at;

      header = (
        <TruckGroupHeader
          title={getTruckTitle(truck)}
          color={truck.color}
          summary={[
            `${group.all.length > 0 ? blCount : 0} BL`,
            formatWeight(weight),
            distance > 0 && formatKm(distance),
            isUnavailable && truck.unavailabilityReason,
            isUnavailable &&
              group.all.length > 0 &&
              `${group.all.length} à déplacer`,
          ]
            .filter(Boolean)
            .join(' · ')}
          load={{ weight, capacity: truck.capacity }}
          isUnavailable={isUnavailable}
          badge={
            isUnavailable
              ? {
                  label:
                    truck.eligibilityStatus === ELIGIBILITY.UNAVAILABLE
                      ? 'Indisponible'
                      : 'Non proposé',
                  title: describeTruckUnavailability(truck),
                }
              : hasNoTimeWindow
                ? {
                    label: 'Sans horaires',
                    title:
                      "Ce véhicule n'a pas d'horaires de départ/fin définis. Les ETA calculées seront incorrectes.",
                  }
                : null
          }
          dispatchBadge={
            truck.isLocked
              ? {
                  label: dispatchedAt
                    ? `Envoyé · ${moment(dispatchedAt).format('HH:mm')}`
                    : 'Envoyé',
                  title: `Tournée ${truck.dispatchState.tour_key} envoyée en exploitation : le camion est verrouillé. Pour le modifier, supprimez sa tournée (non démarrée) depuis la liste des tournées.`,
                }
              : null
          }
          selectionState={getGroupSelectionState(group.rows)}
          onToggleSelect={(checked) => toggleSelection(visibleIds, checked)}
          action={{
            label: "Optimiser l'ordre",
            onClick: () => handleOptimizeVehicle(truck),
            disabled:
              isOptimizingVehicle || group.all.length === 0 || truck.isLocked,
          }}
          menuItems={getTruckMenuItems(truck, group.all)}
          menuFooter="Les actions suivies de « … » ouvrent une confirmation. Sur un camion vide, « Vider » est désactivé."
          {...(truck.isLocked ? {} : dropHandlers)}
        />
      );
    }

    return (
      <div key={group.key} className="ta-list__group">
        {header}
        {group.isUnassigned && unplacedBanner && (
          <div className="ta-banner ta-banner--warning">
            <span className="ta-banner__icon">⚠</span>
            <span>
              {unplacedBanner.unplaced} non placée
              {unplacedBanner.unplaced > 1 ? 's' : ''}
              {" par l'optimisation"}
            </span>
            {unplacedBanner.overCapacity > 0 && (
              <>
                <span className="ta-banner__divider" />
                <span>{unplacedBanner.overCapacity} hors capacité</span>
              </>
            )}
            <span className="ta-spacer" />
            <button
              type="button"
              className="ta-link"
              onClick={() => setActiveTab(TABS.ALERTS)}
            >
              Voir dans Alertes
            </button>
            <button
              type="button"
              className="ta-link ta-link--muted"
              onClick={() => setUnplacedBanner(null)}
            >
              Ignorer
            </button>
          </div>
        )}
        {depotRows?.departure}
        {!(group.isUnassigned && isUnassignedCollapsed) &&
          group.rows.map((delivery) => (
            <AssignmentRow
              key={delivery.tourPlanningId}
              delivery={delivery}
              color={group.truck?.color}
              alerts={alertsById.get(delivery.tourPlanningId) || []}
              isSelected={selectedIds.has(delivery.tourPlanningId)}
              isOpened={openedId === delivery.tourPlanningId}
              isLocked={Boolean(group.truck?.isLocked)}
              onOpen={() => openDelivery(delivery)}
              onToggleSelect={(checked) =>
                toggleSelection([delivery.tourPlanningId], checked)
              }
              onDragStart={(event) => handleDragStart(event, delivery)}
              onDragEnd={handleDragEnd}
              dropPosition={
                rowDropTarget?.id === delivery.tourPlanningId
                  ? rowDropTarget.position
                  : null
              }
              dropHandlers={getRowDropHandlers(delivery, group)}
              menuItems={getRowMenuItems(delivery)}
            />
          ))}
        {depotRows?.return}
      </div>
    );
  };

  const renderListContent = () => {
    if (isLoadingScheduled && deliveries.length === 0) {
      return (
        <div className="ta-list__loading">
          <GenericTableSkeleton numberOfRows={8} />
        </div>
      );
    }

    if (deliveries.length === 0) {
      return (
        <div className="ta-empty">
          <span className="ta-empty__icon">○</span>
          <span className="ta-empty__title">
            Aucune commande le {moment(selectedDate).format('ddd D MMM YYYY')}
          </span>
          <span className="ta-empty__text">
            Les commandes planifiées arrivent depuis le puits de commandes.
          </span>
          <button
            type="button"
            className="ta-link"
            onClick={() => navigate('/orders-pool')}
          >
            Ouvrir le puits de commandes ›
          </button>
        </div>
      );
    }

    if (groups.every((group) => group.rows.length === 0) && search.trim()) {
      return (
        <div className="ta-empty">
          <span className="ta-empty__icon">⌕</span>
          <span className="ta-empty__title">Aucun résultat</span>
          <span className="ta-empty__text">
            Aucune commande ne correspond à « {search.trim()} ».
          </span>
        </div>
      );
    }

    if (groups.length === 0) {
      return (
        <div className="ta-empty">
          <span className="ta-empty__icon">✓</span>
          <span className="ta-empty__title">
            Aucune commande dans cette vue
          </span>
        </div>
      );
    }

    return groups.map(renderGroup);
  };

  return (
    <div className="ta-page">
      <PageHeader
        totalCount={deliveries.length}
        summary={headerSummary}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        alertsCount={alertsCount}
        onExport={handleExportAssignments}
        canExport={deliveries.length > 0}
        onOpenVehiclesOfTheDay={
          hasVehiclesOfTheDay ? vehiclesOfTheDay.openPanel : undefined
        }
        dispatchAction={
          isDispatchEnabled
            ? {
                onClick: () => setDispatchModal({ onlyVehicleKey: null }),
                disabled: isDispatching || draftTrucksCount === 0,
                title:
                  draftTrucksCount === 0
                    ? 'Aucun camion en préparation à cette date'
                    : `${draftTrucksCount} camion${
                        draftTrucksCount > 1 ? 's' : ''
                      } en préparation à envoyer`,
              }
            : null
        }
      />

      <FiltersBar
        search={search}
        onSearchChange={setSearch}
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
        calendarDays={calendarDays}
        onCalendarMonthChange={(date) =>
          setCalendarMonth(moment(date).format('YYYY-MM'))
        }
        trucks={trucks}
        truckCounts={truckCounts}
        selectedVehicleIds={selectedVehicleIds}
        onVehicleIdsChange={setSelectedVehicleIds}
        onResetFilters={() => {
          setSearch('');
          setSelectedVehicleIds(new Set());
          setHideEmptyTrucks(false);
        }}
        hideEmptyTrucks={hideEmptyTrucks}
        onHideEmptyTrucksChange={setHideEmptyTrucks}
      />

      <div className="ta-columns">
        <div className="ta-list-column">
          <div
            className="ta-list"
            ref={listRef}
            onDragOverCapture={handleListDragOverCapture}
          >
            {renderListContent()}
          </div>
          <SelectionBar
            selectedDeliveries={selectedDeliveries}
            trucks={trucks}
            onAssign={handleOpenBulkAssignmentModal}
            onReschedule={() =>
              setRescheduleIds(selectedDeliveries.map((d) => d.tourPlanningId))
            }
            onReturnToPool={() => handleReturnToPool(selectedDeliveries)}
            onUnassign={() => {
              const assigned = selectedDeliveries.filter((d) => d.vehicleId);
              if (
                window.confirm(
                  `Désaffecter ${assigned.length} commande${
                    assigned.length > 1 ? 's' : ''
                  } de leur camion ?`
                )
              ) {
                unassignDeliveries(assigned).then(() =>
                  setSelectedIds(new Set())
                );
              }
            }}
            onClear={() => setSelectedIds(new Set())}
            isBusy={isBusy}
          />
        </div>

        <AssignmentsMap
          deliveries={deliveries}
          trucks={trucks}
          factories={factories}
          routes={optimizedRoutes}
          selectedVehicleIds={selectedVehicleIds}
          onAssignDelivery={handleOpenAssignmentModal}
          fitKey={`${tourDate}|${[...selectedVehicleIds].join(',')}|${
            deliveries.length > 0
          }`}
        />
      </div>

      {optimizationToast && (
        <div className="ta-toast" role="status">
          <span className="ta-toast__dot" />
          <span>
            Optimisation terminée · {optimizationToast.routes} tournée
            {optimizationToast.routes > 1 ? 's' : ''} ·{' '}
            {optimizationToast.orders} commande
            {optimizationToast.orders > 1 ? 's' : ''}
            {optimizationToast.distance > 0 &&
              ` · ${formatKm(optimizationToast.distance)}`}
            {optimizationToast.duration > 0 &&
              ` · ${formatDuration(optimizationToast.duration)}`}
          </span>
          <button
            type="button"
            className="ta-toast__close"
            onClick={() => setOptimizationToast(null)}
            title="Fermer"
          >
            ✕
          </button>
        </div>
      )}

      {openedDelivery && (
        <AssignmentDrawer
          delivery={openedDelivery}
          truck={truckById.get(openedDelivery.vehicleId)}
          details={detailsData}
          isLoadingDetails={isDetailsLoading}
          menuItems={getRowMenuItems(openedDelivery)}
          onClose={closeDrawer}
        />
      )}

      {vehicleDetailsTruck && (
        <EditVehicleModal
          isOpen
          setIsOpen={(open) => {
            if (open) return;
            setVehicleDetailsTruck(null);
            // Horaires ou capacité ont pu changer
            refreshData();
          }}
          vehicle={{
            vehicule_id: vehicleDetailsTruck.id,
            vehicule_key: vehicleDetailsTruck.key,
            imat: vehicleDetailsTruck.licensePlate,
          }}
        />
      )}

      <ManualAssignmentModal
        isOpen={showAssignmentModal}
        onClose={handleCloseAssignmentModal}
        deliveries={selectedDeliveryForAssignment}
        // Un camion envoyé en exploitation ne reçoit plus de commande, ni un
        // camion indisponible ce jour (véhicules du jour)
        trucks={
          hasVehiclesOfTheDay
            ? getAssignableTrucks(editableTrucks, selectedDeliveryForAssignment)
            : editableTrucks
        }
        onAssign={handleManualAssignment}
        isAssigning={isAssigning}
        autoOptimize={autoOptimizeAfterAssignment}
        onAutoOptimizeChange={setAutoOptimizeAfterAssignment}
      />

      {hasVehiclesOfTheDay && vehiclesOfTheDay.isPanelOpen && (
        <VehiclesOfTheDayPanel
          date={tourDate}
          truckCounts={truckCounts}
          refreshKey={vehiclesOfTheDay.refreshKey}
          isBusy={vehiclesOfTheDay.isSaving}
          onClose={vehiclesOfTheDay.closePanel}
          onMarkUnavailable={(vehicle) =>
            vehiclesOfTheDay.openMarkUnavailable({
              ...vehicle,
              assignedCount: truckCounts[vehicle.id] || 0,
            })
          }
          onMakeAvailable={vehiclesOfTheDay.makeAvailable}
          onOpenVehicle={(vehicle) => {
            vehiclesOfTheDay.closePanel();
            setVehicleDetailsTruck(vehicle);
          }}
        />
      )}

      {hasVehiclesOfTheDay && (
        <UnavailableModal
          vehicle={vehiclesOfTheDay.markTarget}
          date={tourDate}
          assignedCount={vehiclesOfTheDay.markTarget?.assignedCount || 0}
          onClose={vehiclesOfTheDay.closeMarkUnavailable}
          onConfirm={vehiclesOfTheDay.markUnavailable}
        />
      )}

      <RescheduleDateModal
        isOpen={Boolean(rescheduleIds)}
        onClose={() => setRescheduleIds(null)}
        stepsCount={rescheduleIds?.length || 0}
        onReschedule={handleReschedule}
        isRescheduling={isRescheduling}
      />

      {isDispatchEnabled && (
        <DispatchModal
          isOpen={Boolean(dispatchModal)}
          onClose={() => setDispatchModal(null)}
          date={tourDate}
          trucks={trucks}
          onlyVehicleKey={dispatchModal?.onlyVehicleKey || null}
          loadPreview={previewDispatch}
          onConfirm={handleDispatch}
        />
      )}
    </div>
  );
}
